// Umag torna konzultáció — the consultation article, ported to React from
// PairMaker/umag.html. Static prose is real JSX (math via String.raw so the
// LaTeX braces don't collide with JSX); the calculator, heatmaps and decision
// tree are interactive React components. Styles live in ./cikk.css, scoped
// under .cikk-root.
import { useRef } from 'react';

import './cikk.css';
import CikkHeatmaps from './cikk/CikkHeatmaps';
import CikkCalculator from './cikk/CikkCalculator';
import CikkRankSlope from './cikk/CikkRankSlope';
import CikkDecisionTree from './cikk/CikkDecisionTree';
import { useMathJax } from './cikk/useMathJax';

export default function KonzultacioPage() {
  const articleRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  useMathJax(articleRef, tooltipRef);

  return (
    <div className="cikk-root">
      <div className="page">
        <header className="masthead">
          <p className="eyebrow">Konzultáció · 2026</p>
          <h1>
            Umag torna
            <br />
            <em>konzultáció</em>
          </h1>
          <p className="meta">A párok összeállításától a győztes hirdetéséig</p>
        </header>

        <main>
          <article ref={articleRef}>
            {/* INTRO */}
            <section className="section">
              <div className="col">
                <p className="lead">
                  Az umagi nyaralás sava-borsa a teniszverseny, ami évről évre fejlődik a
                  résztvevőkkel együtt. Már tavaly is eszünkbe jutott, hogy hogyan lehetne
                  még jobbá tenni, de az idő hiányában a hagyományos, ad-hoc jelleggel
                  kialakult módszerekhez nyúlt vissza a szervezés annak minden előnyével és
                  hátrányával együtt.
                </p>
                <p>
                  Ezért annak érdekében, hogy jövőre is sikerüljön megugrani a már eddig is
                  magasra emelt lécet, elkészítettük ezt a dokumentumot, hogy kikérhessük a
                  közösség véleményét a verseny lebonyolításáról a párok összeállításától a
                  győztes hirdetéséig. A folyamat széleskörűbb egyeztetése átláthatóbbá
                  teszi a rendszert, kiemel rejtett igényeket, amik eddig nem kaptak
                  érvényt, ezáltal igazságosabbá, izgalmasabbá és élvezetesebbé teszi a
                  versenyt.
                </p>
                <p>
                  Azzal az alapfeltevéssel élünk, hogy körülbelül 5–6 napon keresztül három
                  pályán egy délelőtti és egy délutáni kétórás sávban zajlanak a hivatalos
                  meccsek, ez összesen 30–36 meccset jelent, azaz minden párosra
                  teljesítménytől függően 5–7-et. A továbbiakban a fő kérdések mentén
                  felvázoljuk a lehetőségeket és azok életszerű kombinációit, majd egy
                  szavazással lehet véleményt és igényt nyilvánítani.
                </p>
              </div>
            </section>

            {/* SECTION I: PÁROK ÖSSZEÁLLÍTÁSA */}
            <section className="section">
              <div className="col">
                <header className="section-header">
                  <span className="section-num">Első rész</span>
                  <h2>Párok összeállítása</h2>
                </header>

                <p>
                  Az egész verseny kardinális kérdése és megalapozója, hogy ki-kivel van
                  párban, ezért fontos, hogy a lehető legtöbbet hozzuk ki a
                  lehetőségeinkből. Adottság, hogy a társaság nagyon színes és hatalmas
                  skálán mozog a tenisz képességek terén, ezért különös kihívás olyan
                  rendszert alkotni, ami igazságos, átlátható és izgalmas, és jó
                  párosításokat eredményez.
                </p>

                <h3>A.) Legyenek fix párok?</h3>

                <div className="option option--stub">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-A1">
                      A1
                    </span>
                    Igen.
                  </h4>
                </div>
                <div className="option option--stub">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-A2">
                      A2
                    </span>
                    Nem.
                  </h4>
                </div>

                <p>
                  A verseny egészét alapjaiban változtatná meg, ha az erre a kérdésre a
                  többség nemmel válaszolna, megszűnne a párválasztás súlya és sok
                  mindenkivel ki lehetne próbálni a közös játékot. Viszont ez így egy
                  párosban játszott egyéni versenyt jelentene, és a teniszpáros egy
                  csapatsport. És az Umagban töltött egy hét alatt lehetőség van arra, hogy
                  egy-két játékos összeszokjon és a párosnak pont a sava-borsa a
                  csapatösszhang, ami egy meccs alatt nehezen alakulna ki, de biztos nem
                  fejlődne arra a szintre, mint egy hét közös küzdelem alatt.
                </p>

                <h3>B.) Mi alapján legyenek a párok összerakva?</h3>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-B1">
                      B1
                    </span>
                    Areiosz Pagosz, a vének tanácsa
                  </h4>
                  <p>
                    Eddig ez volt az alkalmazott módszer. Néhány kiválasztott végtelen
                    jószándékkal önkényesen meghatározott egy ordinális sorrendet és
                    igyekezett hasonló erejű játékosokat összepárosítani, figyelembe venni,
                    hogy ki kivel és kivel nem szeretne együtt lenni. A döntéshez
                    felhasználta az összes szubjektív megérzést, ami létezik és
                    összehasonlításokat végzett ezek alapján, amíg kialakult a legkevesebb
                    elégedetlenséget kiváltó felállás.
                  </p>
                  <p>
                    Ennek a módszernek az előnye az, hogy bizonyítottan működik, viszont 10
                    páros összeállításánál a{' '}
                    {String.raw`$\frac{(2n)!}{2^n \cdot n!}$`} képlet alapján{' '}
                    {String.raw`$\frac{20!}{2^{10} \cdot 10!}$`}, azaz{' '}
                    <strong>654&nbsp;729&nbsp;075</strong> lehetséges párosítás létezik, 24
                    embernél <strong>316&nbsp;234&nbsp;143&nbsp;225</strong>. Ezáltal
                    különös kihívás levezetni a párosítással elégedetlen versenyzőknek, hogy
                    a több százmillió vagy esetleg milliárd lehetőség közül miért pont erre
                    esett a választás és miért nem egy másikra, illetve valószínűtlen, hogy
                    a legjobb megoldás került kiválasztásra.
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-B2">
                      B2
                    </span>
                    Sorsolás
                  </h4>

                  <h4>
                    <span className="option-tag" id="opt-B2a">
                      B2a
                    </span>
                    Egykalapos sorsolás
                  </h4>
                  <p>
                    Igazságos módszer, viszont az így keletkező párok a legkisebb
                    valószínűséggel lesznek kiegyenlítettek. Két gyengébb játékos nem
                    biztos, hogy annyira élvezné a versenyt együtt, de ha a két legerősebb
                    összekerül, lehet nekik is hiányozna a kihívás.
                  </p>

                  <h4>
                    <span className="option-tag" id="opt-B2b">
                      B2b
                    </span>
                    Kétkalapos sorsolás (vegyes)
                  </h4>
                  <p>
                    A versenyzőket egy erős és még erősebb csoportba osztályozzuk,
                    önbevallás/csoportos szavazás/önkény alapján és a páros úgy jön létre,
                    hogy mindkét csapatból kiválasztunk egy-egy résztvevőt. Az így létrejövő
                    párosok kiegyenlítettebbek, de szintén valószínűtlen az optimális
                    megoldás feltéve ha cél, hogy minél kiegyenlítettebb legyen a verseny.
                  </p>

                  <h4>
                    <span className="option-tag" id="opt-B2c">
                      B2c
                    </span>
                    Kétkalapos sorsolás (kalapon belül)
                  </h4>
                  <p>
                    A versenyzőket az előző módon kétfelé osztjuk, viszont a párosok úgy
                    születnek, hogy a kalapon belül választunk párt, így létrejön egy erős
                    csoport az első kalapból és egy még erősebb a másodikból. Így a
                    csoporton belül kiegyenlített párosítások vannak és izgalmas meccsek
                    lesznek, két csoport segítségével könnyebb a csoporton belüli
                    különbségeket minimalizálni, mint egy csoporton belül.
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-B3">
                      B3
                    </span>
                    Adat alapú párosítás
                  </h4>
                  <p>
                    Az Areiosz Pagosz módszerből kiemelnénk a felhalmozott bölcsességet és
                    felhatalmaznánk a közösséget, közösen mérjék fel az erőviszonyokat. Így
                    a lehető legtöbb helyről érkezik információ, azok, akik egymással gyakran
                    játszanak, viszonylag reális képet tudnak adni a másik játékosról és
                    össze tudják őket hasonlítani. És minél nagyobb a részvétel, annál
                    pontosabb képet kapunk. Az értékelések nyilvánosak, de az értékelő
                    személye rejtett (szervezők számára ismert) és kompenzálunk az ellen,
                    hogy senki ne szenvedjen hátrányos megkülönböztetést saját magától, így
                    egy viszonylag transzparens képet kapunk a mezőnyről a párok
                    összeállításához. Ezt követően egy programmal optimális megoldást
                    keresünk arra a problémára, hogy minél kisebb legyen a csoporton (vagy
                    két csoporton) belüli erőkülönbség.
                  </p>

                  <h4>
                    <span className="option-tag" id="opt-B3a">
                      B3a
                    </span>
                    Közösségi ordinális sorrendállítás
                  </h4>
                  <p>
                    Legegyszerűbb módszer, mindenki felállít egy sorrendet az általa ismert
                    játékosokról (magát leszámítva) és ezeket összesítjük. Ez a módszer nem
                    ad képet arról, hogy két hely között is lehet akkora erőkülönbség, mint
                    4-5 között, akik viszonylag hasonló szinten vannak.
                  </p>

                  <h4>
                    <span className="option-tag" id="opt-B3b">
                      B3b
                    </span>
                    Közösségi sorrendállítás Élő pontrendszerrel
                  </h4>
                  <p>
                    Azokat a játékosokat hasonlíttatjuk össze egy programmal, akiket a
                    szavazó ismer (Ki nyerne? Aladár vagy Béla — döntetlen megengedett). Így
                    kialakul egy árnyaltabb rendszer rejtett Élő pontokkal (mindenkinek
                    kérés szerint eláruljuk a sajátját), így nem csak egy egyszerű sorrend
                    áll fel, de az erőkülönbségeket is viszonylag jól méri ezáltal pontosabb
                    eredményt kapunk.
                  </p>

                  <aside className="digression">
                    <p className="digression-label">Kitérő</p>
                    <h4>Mi is az az Élő pont?</h4>
                    <p>
                      Az 1960-as években Élő Árpád találta ki arra a problémára, hogy hogyan
                      állítsanak fel egy rangsort a sakkozók között.
                    </p>

                    <div className="formula-block">
                      {String.raw`$$\hv{Ea}{E_a} = \frac{1}{1 + 10^{(\hv{Rb}{R_b} - \hv{Ra}{R_a})/400}}$$`}
                    </div>

                    <p>ahol:</p>
                    <ul>
                      <li>
                        {String.raw`$E_a$`} a várható értéke a mérkőzésnek (0 — biztos
                        vereség, 1 — biztos győzelem, 0,34 — 34%-os győzelem)
                      </li>
                      <li>{String.raw`$R_b$`} „B” játékos értékelése (Élő pontja)</li>
                      <li>{String.raw`$R_a$`} „A” játékos értékelése</li>
                      <li>
                        400 egy skálázási döntés, 400 pont különbség azt jelenti, hogy az
                        erősebb játékos 10-ből 9-szer legyőzi a gyengébbet, 200 pont 76% és
                        0 pont 50%
                      </li>
                    </ul>

                    <p>A mérkőzés kimenetele után módosulnak a pontok:</p>

                    <div className="formula-block">
                      {String.raw`$$\hv{RaP}{R_a'} = \hv{Ra}{R_a} + \hv{K}{K} \cdot (\hv{Sa}{S_a} - \hv{Ea}{E_a})$$`}
                    </div>

                    <p>ahol</p>
                    <ul>
                      <li>{String.raw`$R_a'$`} „A” játékos új értékelése</li>
                      <li>{String.raw`$R_a$`} „A” játékos mérkőzés előtti értékelése</li>
                      <li>
                        {String.raw`$K$`} az egy faktor, ami azt kontrollálja, hogy milyen
                        gyorsan változik az értékelés, nagyobb K-t ismeretlen játékosoknál
                        érdemes használni, hogy gyorsabban beálljon a valósághoz közeli
                        értékelés, kisebbet pedig akkor, amikor már ismert és „be van
                        kalibrálva” így egy rossz meccs nem fogja nagyon lerontani, de sok
                        eredmény így is változtat
                      </li>
                      <li>
                        {String.raw`$S_a$`} a meccs kimenetele (1 — győzelem, 0 — vereség,
                        0,5 — döntetlen)
                      </li>
                    </ul>

                    <div className="example">
                      <p className="example-label">Példa</p>
                      <p>
                        Aladár Élő pontja 1500, Béláé 1400. Mi a mérkőzés várható
                        kimenetele?
                      </p>

                      <div className="formula-block">
                        {String.raw`$$\hv{Ea}{E_a} = \frac{1}{1 + 10^{(1400-1500)/400}} = \frac{1}{1 + 10^{-0{,}25}} = \frac{1}{1 + 0{,}562} \approx 0{,}64$$`}
                      </div>

                      <p>Tehát Aladárra 64%-os, Bélára 36%-os győzelmi esély adódik.</p>

                      <p>
                        Ha Aladár nyer ({String.raw`$\hv{Sa}{S_a} = 1$`}),{' '}
                        {String.raw`$\hv{K}{K} = 32$`} mellett:
                      </p>
                      <div className="formula-block">
                        {String.raw`$$\hv{RaP}{R_a'} = 1500 + 32 \cdot (1 - 0{,}64) = 1500 + 11{,}5 = 1511{,}5$$
$$\hv{RbP}{R_b'} = 1400 + 32 \cdot (0 - 0{,}36) = 1400 - 11{,}5 = 1388{,}5$$`}
                      </div>
                      <p>Az erősebb játékos várható győzelme csak kis pontmozgást okoz.</p>

                      <p>
                        Ha viszont Béla nyer (a meglepetés esete,{' '}
                        {String.raw`$\hv{Sa}{S_a} = 0$`}):
                      </p>
                      <div className="formula-block">
                        {String.raw`$$\hv{RaP}{R_a'} = 1500 + 32 \cdot (0 - 0{,}64) = 1500 - 20{,}5 = 1479{,}5$$
$$\hv{RbP}{R_b'} = 1400 + 32 \cdot (1 - 0{,}36) = 1400 + 20{,}5 = 1420{,}5$$`}
                      </div>
                      <p>
                        A meglepő eredmény közel kétszer akkora pontmozgást eredményez: Béla
                        20,5 ponttal erősödik, Aladár ugyanennyivel gyengül. Az Élő
                        rendszerben a pontmozgás mindig zéróösszegű — amit az egyik nyer, a
                        másik elveszti.
                      </p>
                    </div>

                    <p>
                      Azért jó, mert nem kell felállítani egy abszolút mércét és ahhoz mérni
                      mindenkit, mert elég egyszerű összehasonlításokat végezni és a sorrend
                      kialakul magától az erőviszonnyal együtt. Nem csak a sakkban
                      használják, de szinte az összes sportban, sportfogadásoknál,
                      kompetitív videojátékoknál, társkereső alkalmazásokban, bor
                      értékeléseknél, zene ajánló rendszerekben, szinte mindenhol, ahol
                      nehéz egy abszolút értékelést felállítani, de az összehasonlítás
                      lehetséges.
                    </p>
                  </aside>

                  <h4>
                    <span className="option-tag" id="opt-B3c">
                      B3c
                    </span>
                    Közösségi sorrendállítás képlettel és képesség pontozással
                  </h4>
                  <p>
                    Egy páros ereje nem egyszerűen a két játékos erejének az összege. A
                    valóság elképesztően bonyolult és összetett, tele van véletlennel,
                    ismert és ismeretlen ismeretlenekkel, ezért nem lehet leírni tökéletesen.
                    Viszont ahogy Magyarországról Umagra sem a Föld felszínét fűszál szinten
                    tanulmányozva jutunk el, hanem egy térkép segítségével (és gps-el), ami
                    egy modell, a valóság leegyszerűsített mása, vetülete bizonyos igények
                    szerint, így a páros erejére is tudunk egy összegnél jobb közelítést
                    adni, ami olyan dinamikákat figyelembe tud venni, hogy egy erős röpte
                    kompenzálhat egy gyenge szervát, vagy azt, hogyha a csapatban van egy
                    fürgelábú játékos az szerencsésebb, mint ha egy sem lenne. Íme a képlet:
                  </p>

                  <div className="formula-block">
                    {String.raw`$$\begin{aligned}
                    \text{Páros erőssége} = {} & (\hv{sz1}{sz_1} + \hv{a1}{a_1} + 0{,}5)(\hv{r2}{r_2} + 1) + (\hv{sz2}{sz_2} + \hv{a2}{a_2} + 0{,}5)(\hv{r1}{r_1} + 1) \\
                    & + (\hv{a1}{a_1} + 1)(\hv{r2}{r_2} + 1) + (\hv{a2}{a_2} + 1)(\hv{r1}{r_1} + 1) \\
                    & + \hv{bonusz}{\sum \text{bónusz}} - \hv{buntet}{\sum \text{büntető}}
                    \end{aligned}$$`}
                  </div>

                  <dl className="var-legend">
                    <dt>sz</dt>
                    <dd>szerva</dd>
                    <dt>a</dt>
                    <dd>alapjáték</dd>
                    <dt>r</dt>
                    <dd>röpte</dd>
                    <dt>1, 2 alsó indexek</dt>
                    <dd>a páros első, illetve második tagja</dd>
                  </dl>

                  <h4>Honnan jön a képlet?</h4>

                  <p>
                    Azt hivatott modellezni, hogy a tenisz meccs alatt a különböző
                    fázisokban a páros tagjainak mely képessége érvényesül a legjobban és
                    mivel járul hozzá a csapat teljesítményhez. Például, a képlet első
                    tagja, amikor a csapat első tagja szervál:
                  </p>

                  <div className="formula-block">
                    {String.raw`$$(\hv{sz1}{sz_1} + \hv{a1}{a_1} + 0{,}5)(\hv{r2}{r_2} + 1)$$`}
                  </div>

                  <p>ahol</p>
                  <ul>
                    <li>{String.raw`$sz_1$`} — a csapat első tagjának szervája</li>
                    <li>{String.raw`$a_1$`} — a csapat első tagjának az alapvonal játéka</li>
                    <li>{String.raw`$r_2$`} — a csapat második tagjának a röptéje</li>
                    <li>
                      0,5 és 1 azok segéd konstansok, hogy a szorzat lehetőleg egynél
                      nagyobb legyen
                    </li>
                  </ul>

                  <p>
                    A képlet második tagja,{' '}
                    {String.raw`$(\hv{sz2}{sz_2} + \hv{a2}{a_2} + 0{,}5)(\hv{r1}{r_1} + 1)$`}{' '}
                    logikailag ugyanúgy épül fel, mint az első, a különbség az, hogy a
                    csapat második tagja szervál és az első röptézik elől.
                  </p>

                  <p>
                    A képlet harmadik tagja,{' '}
                    {String.raw`$(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1) + (\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)$`}{' '}
                    a következőképpen jön ki:
                  </p>

                  <p>
                    Amikor az ellenfél szervál a pont felében a tenyeres oldalon lévő
                    játékos fogad és áll az alapvonalon és a fonák oldalon lévő röptézik:
                  </p>

                  <div className="formula-block">
                    {String.raw`$$\frac{(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1)}{2}$$`}
                  </div>

                  <p>A pont másik felében pedig fordítva:</p>

                  <div className="formula-block">
                    {String.raw`$$\frac{(\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)}{2}$$`}
                  </div>

                  <p>
                    Tehát:{' '}
                    {String.raw`$\frac{(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1)}{2} + \frac{(\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)}{2}$`}
                  </p>

                  <p>
                    De egy szerva körben kétszer fordul elő, hogy az ellenfél csapata
                    szervál, ezért ha hozzáadjuk a másik alkalmat is azt kapjuk, hogy
                  </p>

                  <div className="formula-block">
                    {String.raw`$$\frac{(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1)}{2} + \frac{(\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)}{2} + \frac{(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1)}{2} + \frac{(\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)}{2}$$`}
                  </div>

                  <p>ami egyszerűsítve:</p>

                  <div className="formula-block">
                    {String.raw`$$(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1) + (\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)$$`}
                  </div>

                  <p>
                    A {String.raw`$\sum \text{bónusz}$`} az a csapat játékosainak bónusz
                    pontjainak összege, három féle bónusz pont van:
                  </p>
                  <ul>
                    <li>gyors</li>
                    <li>kiegyensúlyozott</li>
                    <li>ravasz</li>
                  </ul>

                  <p>
                    Ezek a modellben binárisak, azaz igen/nem → 0 vagy 1 értéket vesznek
                    fel. Gyors játékosnak az számít, aki be tudja futni a pályát és elér
                    rövidítéseket, átemeléseket. Kiegyensúlyozott az, aki mind
                    játékszínvonalában és pszichésen is stabil, tudja tartani a lelket a
                    másikban és jól teljesít nyomás alatt. Ravasz az, akinek a játéka
                    kellemetlen, tud „okosan” játszani és taktikázni.
                  </p>

                  <p>
                    A büntető pontok arra szolgálnak, hogy beépüljön a képletbe az a
                    szcenárió, ha nincs a csapatban gyors játékos, vagy egyik csapattag sem
                    rendelkezik a kiegyensúlyozott tulajdonsággal. Könnyű belátni, hogy két
                    gyors játékos jobb, mint egy, de ha nincs senki, aki el tudna menni a
                    labdáért, az rosszabb, mint amennyivel jobb, ha két ember is el tud menni
                    az egyhez képest.
                  </p>

                  <p>
                    Eddig több volt a betű, mint a szám, ezért nézzük meg, hogy értékekkel
                    behelyettesítve milyen eredményeket ad a képlet. Legyen a skálánk a példa
                    kedvéért 1–5, de lehetne 1–7, 1–10 is. Az egyszerűség kedvéért most a
                    játékosok összes képessége egyforma (alapjáték, szerva, röpte), tehát, ha
                    most úgy hivatkozunk egy játékosra, hogy 5, akkor neki a szervája 5, alap
                    vonal játéka 5 és a röptéje is 5. Az értékek normalizálva, azaz a
                    maximális értékkel elosztva szerepelnek, hogy 0 és 1 között legyenek.
                    Bónuszok binárisak, azaz 0 vagy 1. Kezdjünk extrém példákkal, mi
                    történik, ha két csupa 5-ös, két csupa egyes, egy csupa ötös és egy csupa
                    1-es vagy két csupa hármas játékos áll össze?
                  </p>

                  <p>A képlet szerint:</p>

                  <h4>
                    A pár első tagjának a szervája alatt:{' '}
                    {String.raw`$(\hv{sz1}{sz_1} + \hv{a1}{a_1} + 0{,}5)(\hv{r2}{r_2} + 1)$`}
                  </h4>

                  <table className="calc-table">
                    <thead>
                      <tr>
                        <th>Pár</th>
                        <th>Levezetés</th>
                        <th>Eredmény</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pair">5–5</td>
                        <td className="calc">(5/5 + 5/5 + 0,5)(5/5 + 1) = (1 + 1 + 0,5)(1 + 1) = 2,5 · 2</td>
                        <td className="total">5</td>
                      </tr>
                      <tr>
                        <td className="pair">3–3</td>
                        <td className="calc">(3/5 + 3/5 + 0,5)(3/5 + 1) = (0,6 + 0,6 + 0,5)(0,6 + 1) = 1,7 · 1,6</td>
                        <td className="total">2,72</td>
                      </tr>
                      <tr>
                        <td className="pair">1–1</td>
                        <td className="calc">(1/5 + 1/5 + 0,5)(1/5 + 1) = (0,2 + 0,2 + 0,5)(0,2 + 1) = 0,9 · 1,2</td>
                        <td className="total">1,08</td>
                      </tr>
                      <tr>
                        <td className="pair">5–1</td>
                        <td className="calc">(5/5 + 5/5 + 0,5)(1/5 + 1) = (1 + 1 + 0,5)(0,2 + 1) = 2,5 · 1,2</td>
                        <td className="total">3</td>
                      </tr>
                      <tr>
                        <td className="pair">5–3</td>
                        <td className="calc">(5/5 + 5/5 + 0,5)(3/5 + 1) = (1 + 1 + 0,5)(0,6 + 1) = 2,5 · 1,6</td>
                        <td className="total">4</td>
                      </tr>
                      <tr>
                        <td className="pair">3–1</td>
                        <td className="calc">(3/5 + 3/5 + 0,5)(1/5 + 1) = (0,6 + 0,6 + 0,5)(0,2 + 1) = 1,7 · 1,2</td>
                        <td className="total">2,04</td>
                      </tr>
                    </tbody>
                  </table>

                  <h4>
                    A pár második tagjának a szervája alatt:{' '}
                    {String.raw`$(\hv{sz2}{sz_2} + \hv{a2}{a_2} + 0{,}5)(\hv{r1}{r_1} + 1)$`}
                  </h4>

                  <table className="calc-table">
                    <thead>
                      <tr>
                        <th>Pár</th>
                        <th>Levezetés</th>
                        <th>Eredmény</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pair">5–5</td>
                        <td className="calc">ugyanaz, mint az előzőnél</td>
                        <td className="total">5</td>
                      </tr>
                      <tr>
                        <td className="pair">3–3</td>
                        <td className="calc">ugyanaz, mint az előzőnél</td>
                        <td className="total">2,72</td>
                      </tr>
                      <tr>
                        <td className="pair">1–1</td>
                        <td className="calc">ugyanaz, mint az előzőnél</td>
                        <td className="total">1,08</td>
                      </tr>
                      <tr>
                        <td className="pair">5–1</td>
                        <td className="calc">(1/5 + 1/5 + 0,5)(5/5 + 1) = (0,2 + 0,2 + 0,5)(1 + 1) = 0,9 · 2</td>
                        <td className="total">1,8</td>
                      </tr>
                      <tr>
                        <td className="pair">5–3</td>
                        <td className="calc">(3/5 + 3/5 + 0,5)(5/5 + 1) = (0,6 + 0,6 + 0,5)(1 + 1) = 1,7 · 2</td>
                        <td className="total">3,4</td>
                      </tr>
                      <tr>
                        <td className="pair">3–1</td>
                        <td className="calc">(1/5 + 1/5 + 0,5)(3/5 + 1) = (0,2 + 0,2 + 0,5)(0,6 + 1) = 0,9 · 1,6</td>
                        <td className="total">1,44</td>
                      </tr>
                    </tbody>
                  </table>

                  <h4>
                    Az ellenfél szervája alatt:{' '}
                    {String.raw`$\frac{(\hv{a1}{a_1}+1)(\hv{r2}{r_2}+1)}{2} + \frac{(\hv{a2}{a_2}+1)(\hv{r1}{r_1}+1)}{2}$`}{' '}
                    (egy szerva körre vetítve)
                  </h4>

                  <table className="calc-table">
                    <thead>
                      <tr>
                        <th>Pár</th>
                        <th>Levezetés</th>
                        <th>Eredmény</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pair">5–5</td>
                        <td className="calc">(1+1)(1+1)/2 + (1+1)(1+1)/2 = 4/2 + 4/2</td>
                        <td className="total">4</td>
                      </tr>
                      <tr>
                        <td className="pair">3–3</td>
                        <td className="calc">(1,6)(1,6)/2 + (1,6)(1,6)/2 = 2,56/2 + 2,56/2</td>
                        <td className="total">2,56</td>
                      </tr>
                      <tr>
                        <td className="pair">1–1</td>
                        <td className="calc">(1,2)(1,2)/2 + (1,2)(1,2)/2 = 1,44/2 + 1,44/2</td>
                        <td className="total">1,44</td>
                      </tr>
                      <tr>
                        <td className="pair">5–1</td>
                        <td className="calc">(2)(1,2)/2 + (1,2)(2)/2 = 2,4/2 + 2,4/2</td>
                        <td className="total">2,4</td>
                      </tr>
                      <tr>
                        <td className="pair">5–3</td>
                        <td className="calc">(2)(1,6)/2 + (1,6)(2)/2 = 3,2/2 + 3,2/2</td>
                        <td className="total">3,2</td>
                      </tr>
                      <tr>
                        <td className="pair">3–1</td>
                        <td className="calc">(1,6)(1,2)/2 + (1,2)(1,6)/2 = 1,92/2 + 1,92/2</td>
                        <td className="total">1,92</td>
                      </tr>
                    </tbody>
                  </table>

                  <h4>Összesítés (két ellenfél-szerva körrel)</h4>

                  <table className="calc-table">
                    <thead>
                      <tr>
                        <th>Pár</th>
                        <th>Levezetés</th>
                        <th>Páros erőssége</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pair">5–5</td>
                        <td className="calc">5 + 5 + 2 · 4</td>
                        <td className="total">18</td>
                      </tr>
                      <tr>
                        <td className="pair">3–3</td>
                        <td className="calc">2,72 + 2,72 + 2 · 2,56</td>
                        <td className="total">10,56</td>
                      </tr>
                      <tr>
                        <td className="pair">1–1</td>
                        <td className="calc">1,08 + 1,08 + 2 · 1,44</td>
                        <td className="total">5,04</td>
                      </tr>
                      <tr>
                        <td className="pair">5–1</td>
                        <td className="calc">3 + 1,8 + 2 · 2,4</td>
                        <td className="total">9,6</td>
                      </tr>
                      <tr>
                        <td className="pair">5–3</td>
                        <td className="calc">4 + 3,4 + 2 · 3,2</td>
                        <td className="total">13,8</td>
                      </tr>
                      <tr>
                        <td className="pair">3–1</td>
                        <td className="calc">2,04 + 1,44 + 2 · 1,92</td>
                        <td className="total">7,32</td>
                      </tr>
                    </tbody>
                  </table>

                  <p>Vegyük észre, hogy:</p>

                  <p>
                    Az 5–5-ös párosnál szerva alatt 5 az összérték, az ellenfél szervája
                    közben viszont csak 4. Ez egybevág a valósággal, hiszen a saját
                    szervájukat általában hozzák az erősebb játékosok.
                  </p>

                  <p>
                    Mi a helyzet a 3–3-nál? 2,72 a 2,56-hoz képest, ugyanígy fennáll ez a
                    jelenség, de kisebb mértékben.
                  </p>

                  <p>
                    Az 1–1-nél viszont már 1,08 a saját szerva alatt és 1,44 az ellenfél
                    szervája alatt. Ez is egybevág a valósággal, egy bizonyos szint alatt az
                    adogatás gyenge pont.
                  </p>

                  <p>Hasonlítsuk össze az 5–1-es párost a 3–3-as párossal:</p>

                  <table className="calc-table">
                    <thead>
                      <tr>
                        <th>Játékfázis</th>
                        <th>5–1-es páros</th>
                        <th>3–3-as páros</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Első tag szervája</td>
                        <td className="total">3</td>
                        <td className="total">2,72</td>
                      </tr>
                      <tr>
                        <td>Második tag szervája</td>
                        <td className="total">1,8</td>
                        <td className="total">2,72</td>
                      </tr>
                      <tr>
                        <td>Ellenfél szervája alatt</td>
                        <td className="total">2,4</td>
                        <td className="total">2,56</td>
                      </tr>
                    </tbody>
                  </table>

                  <p>
                    Szintén valóságszagú eredmények jönnek ki, az 5–1-es csapatnál, amikor
                    az 5-ös játékos szervál az veszélyesebb, mint a 3–3-as csapat szervája,
                    az 5–1-es csapat 1-es játékos szervája alatt pedig egyértelműen
                    gyengébb, mint a 3–3 csapat szervája. Az ellenfél szervája alatt pedig a
                    3–3-as csapat egy hajszállal erősebb, mint az 5–1-es csapat, ez is egy
                    hihető eredmény.
                  </p>

                  <p>
                    Összesítésben az 5–1-es csapat ereje:{' '}
                    {String.raw`$3 + 1{,}8 + 2 \cdot 2{,}4 = 9{,}6$`}, a 3–3-asé pedig
                    10,56. Tehát a modell szerint két közepes játékos együtt erősebb, mint
                    egy nagyon jó és egy nagyon gyenge. Ez is inkább igaz a valóságban, mint
                    nem. Ha a páros erejét a játékosok pontjainak összegeként becsültük
                    volna, akkor egyformára jött volna ki {String.raw`$5 + 1 = 3 + 3$`}.
                  </p>

                  <p>
                    A modellben helyet kapott még három bináris paraméter, (gyors,
                    kiegyensúlyozott, ravasz), ha az előző példát minden egyéb paraméter
                    változatlansága mellett módosítjuk annyival, hogy az 5–1-es csapatban
                    valaki rendelkezik egy ravasz bónuszponttal, akkor az ellenfél szervája
                    alatti játékszakaszra vetítve {String.raw`$(1/4=0{,}25)$`} az 5–1-es
                    csapat átveszi a vezetést az ellenfél szervája alatt egy hajszállal 2,65
                    vs 2,56. Összesítésben ez az egy pont{' '}
                    {String.raw`$9{,}6 + 1 = 10{,}6 > 10{,}56$`} elég arra, hogy az 5–1-es
                    csapat legyen erősebbnek tekintve, de lássuk be, hogy a 0,04 pontos
                    különbség inkább 0.
                  </p>

                  <p>
                    Ha az 5–1-es csapatban az 5-ös játékos gyors is, a 3–3-asban pedig mind
                    a kettő lassú, akkor az összesítés így módosul:{' '}
                    {String.raw`$9{,}6 + 1 = 10{,}6$`} vs{' '}
                    {String.raw`$10{,}56 - 1 = 9{,}56$`}. (Itt büntetőpontot kapott a 3–3-as
                    csapat, mert senki se tudja befutni a pályát.) Ez tulajdonképpen a
                    modellben megfordítja az alaphelyzethez képesti mért erőviszonyokat. Így
                    is viszonylag közel van egymáshoz a két csapat, 1–2 pont erőkülönbség,
                    az is belátható, hogy a&nbsp;
                    <span className="trailing-marker" title="A mondat itt a forrásban félbeszakad">
                      …
                    </span>
                  </p>

                  <p>
                    A modell és a képlet támadható, mi történik, ha ketten folyton
                    felrohannak röptézni, valaki előző este elrontotta a gyomrát egy romlott
                    kalamarival, rosszabbul bírja a meleget. A modellnek nem az a célja, hogy
                    tökéletesen leírja a valóságot, hanem hogy jobban közelítse, mint az
                    alternatív módszerek. A képleten tetszés szerint lehet alakítani, hogy
                    még jobban leírja a valóságot.
                  </p>

                  <CikkHeatmaps />

                  <p>
                    A következő kérdés, amit fel kell tenni, az az, hogy rendben, papíron
                    működik, de ki mondja meg, hogy kinek melyik képessége hány pont? És az
                    mitől lesz pontos? Az Areiosz Pagosz módszerben néhány ember ismeretére
                    hagyatkozunk, ezt itt is lehet alkalmazni, viszont hátrány az, hogy még
                    ezek a központi emberek sem ismerik az egész mezőnyt. Ennél pontosabb
                    képet ad, ha mindenkinek van lehetősége (nem kötelezettsége), hogy
                    felállítsa ezt a képesség pontrendszert azokkal a játékosokkal, akiket
                    ismer. Még arra is van lehetőség, hogy súlyozzuk azt, ha valaki valakivel
                    tavaly játszott utoljára és az alapján pontozná. Ez a fajta szavazás a
                    nyilvánosság számára rejtett névvel, de publikus pontozólapokkal
                    működne, hogy ki lehessen szűrni az egyértelmű manipulációt és akár
                    semmissé lehet tenni. Egy ilyen abszolút pontrendszerbe belehelyezni
                    embereket nem könnyű és még ha nagyobb mintánál az egyéni hibák ki is
                    oltják egymást, nem biztos, hogy ez a módszer adja a legjobb képet a
                    mezőnyről.
                  </p>

                  <h4>
                    <span className="option-tag" id="opt-B3d">
                      B3d
                    </span>
                    Közösségi sorrendállítás képlettel és képesség Élő pontrendszerrel
                  </h4>
                  <p>
                    Itt jön képbe a B3b-ből ismert Élő pontrendszer. Ugyanazzal a képlettel
                    becsüljük a pár ütőképességét, mint az előbb, a különbség viszont az,
                    hogy a képességeket nem egy 1–5-ig (vagy más hasonló) skálán mérjük,
                    hanem Élő pontrendszerben.
                  </p>

                  <p>
                    A felmérésben résztvevőknek ilyen és ehhez hasonló kérdésekre kell csak
                    választ adniuk, miután előszűrtük a résztvevőket aszerint, hogy ki-kinek
                    a játékát ismeri eléggé, hogy szavazzon róla:
                  </p>

                  <div className="example">
                    <p className="example-label">Felmérési kérdések</p>
                    <p>
                      <strong>Ki nyerne egy röptepárbajt? Aladár vagy Béla?</strong>
                    </p>
                    <ul>
                      <li>Nagy valószínűséggel Aladár</li>
                      <li>Inkább Aladár</li>
                      <li>Egyenlő esélyekkel nyerik</li>
                      <li>Inkább Béla</li>
                      <li>Nagy valószínűséggel Béla</li>
                    </ul>

                    <p>
                      <strong>
                        Kinek a szerváját nehezebb visszaadni? Aladárét vagy Béláét?
                      </strong>
                    </p>
                    <ul>
                      <li>Aladárét</li>
                      <li>Nagyjából egyforma</li>
                      <li>Béláét</li>
                    </ul>

                    <p>
                      <strong>Ki szokott többet taktikázni a pályán? Aladár vagy Béla?</strong>
                    </p>
                    <ul>
                      <li>Aladár</li>
                      <li>Nincs számottevő különbség</li>
                      <li>Béla</li>
                    </ul>
                  </div>

                  <p>
                    Ezekre a kérdésekre sokkal könnyebb jól válaszolni, a háttérben pedig a
                    matematika elvégzi a piszkos munkát. Az így született szavazatokat egy
                    algoritmus összesíti, majd a kapott Élő pontokat logaritmikusan
                    normalizáljuk a képesség átlaghoz (0 és 1 körüli értékre varázsoljuk).
                    Végül bedobjuk a korábbi képletet és a számokat egy optimalizáló
                    programba, hogy keresse meg azokat a felállásokat, ahol a legkisebb
                    különbség áll fenn egy (vagy két) csoporton belül a párok között.
                  </p>

                  <p>
                    Ez a program képes arra is, hogy befogadja azokat a feltételeket, hogy
                    ki-kivel nem szeretne együtt lenni (általában a családi béke érdekében),
                    de ennek is egy ésszerű határt kell szabni, hogy papíron se lehessen
                    lehetséges, hogy az álompárját leszámítva valaki mindenkit ebbe a
                    halmazba tegyen.
                  </p>

                  <CikkCalculator />

                  <div className="interactive">
                    <p className="interactive-label">A puding próbája az evés</p>
                    <p className="intro">
                      Idén teszteltük ezt a fajta algoritmust a háttérben, íme a párok
                      erősorrendje:
                    </p>

                    <CikkRankSlope />

                    <p>
                      Ránézésre is látszik, hogy a modell egész jól tippelte meg a valóságot, számokban mérve átlagosan 1,85 helyett tévedett (véletlen 4,3 lenne).
                      A 13 párost összessen 6,2 milliárd féleképpen lehetne sorrendbe állítani, ezekből 1 a 680-ból állna olyan közel a valósághoz, mint az algoritmus előrejelzése.
                      Illetve a két döntős párost is megfelelően előrejelezte.
                    </p>
                    <p>
                      A bemelegítő kupa alatt 13/21 = 61.9%-ban az erősebbnek ítélt páros
                      nyert, a fő verseny alatt a csoport körben 9/14 = 64.3%-ban, a
                      kieséses szakaszban pedig 7/10 = 70%-ban jósolta meg az algoritmus
                      helyesen a győztest. Ha beszámítjuk a különbségeket, akkor az egy
                      alattinál 9/21 = 42.9% (azaz egy pénzfeldobás), 1–2-es
                      erőkülönbségnél 7/11 = 63.6%, 2-es felett pedig 13/13 = 100%-os volt
                      a modell.
                    </p>
                  </div>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-B4">
                      B4
                    </span>
                    Gale-Shapley/Irving algoritmus
                  </h4>
                  <p>
                    Abban az esetben, ha elengedjük azt a feltételezést, hogy minél
                    homogénebb mezőnyt kell létrehozni és teret adunk annak, hogy mindenki
                    nyerni akar és ehhez a számára elérhető legjobb párt szeretné maga mellé,
                    létezik megoldás… Mindenki felállít egy preferenciasorrendet, hogy kivel
                    szeretne lenni és ez alapján egy stabil párosítási algoritmus felállít
                    egy olyan párosítást, ahol semelyik két játékos nem lenne szívesebben
                    egymással, mint a kiosztott partnerével. Ez egy járható út és itt a
                    párokkal való elégedettséget és nem a kiegyenlített párokat maximalizáljuk,
                    de számolni kell azzal, hogy az élvonalban lévő játékosok választásától
                    függően nem lesz kiegyenlített a mezőny.
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION II: VERSENY LEBONYOLÍTÁSA */}
            <section className="section">
              <div className="col">
                <header className="section-header">
                  <span className="section-num">Második rész</span>
                  <h2>Verseny lebonyolítása</h2>
                </header>

                <h3>C.) Milyen legyen a meccsek lebonyolítása?</h3>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-C1">
                      C1
                    </span>
                    Két csoport, csoportmeccsek, elődöntők, döntő
                  </h4>
                  <p>
                    Ez a megszokott, jól bevált módszer, matematikailag is kedvező, hiszen
                    10–12 csapat esetén ez két 5–6 fős csoportot jelent, ami{' '}
                    {String.raw`$\frac{2 \cdot n(n-1)}{2}$`} meccs, ami{' '}
                    {String.raw`$n=5$`}-nél 20, {String.raw`$n=6$`}-nál 30 meccs, ami a
                    verseny lebonyolításához felhasználható kerethez jól igazodik. Előnye,
                    hogy jól tervezhető, ez szempont lehet, ha valaki egy egész napos
                    programot szeretne szervezni a pihenőnapjára (ami előfordulhat).
                  </p>

                  <h4>Legyen egy erősebb és egy gyengébb csoport?</h4>
                  <div className="option option--stub">
                    <h4 className="option-title">
                      <span className="option-tag" id="opt-C1a">
                        C1a
                      </span>
                      Igen
                    </h4>
                  </div>
                  <div className="option option--stub">
                    <h4 className="option-title">
                      <span className="option-tag" id="opt-C1b">
                        C1b
                      </span>
                      Ne
                    </h4>
                  </div>

                  <p>
                    Előnye, hogy hasonló képességű párok mérkőznek egymással a csoportmeccsek
                    belül és minden csapatnak van esélye a győzelemre és a dobogóra
                    csoporttól függetlenül is, azáltal, hogy az elődöntő a két csoport első
                    két-két párosát méri össze. További hátránya vagy előnye, hogy a
                    gyengébb csoportból egy páros garantáltan dobogós az egész mezőnyben. Ez
                    esetleg kellemetlen lehet, ha az erősebb csoportban alulmaradtak
                    legyőznék a gyengébb csoport dobogósát.
                  </p>

                  <p>
                    Nem kötelező az elődöntőt és a döntőt keresztbe szervezni és lehet mind a
                    két csoportban győztest hirdetni, így viszont teljesen kettéválna a
                    társaság és a hivatalos meccsek alatt a két csoport párosai nem
                    mérkőzhetnének meg egymással. Valamint joggal követelhetné a gyengébbnek
                    szánt csoport győztese, hogy megmérkőzhessen a másik első helyezettel,
                    hogy esélye legyen az abszolút 1. hely címre.
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-C2">
                      C2
                    </span>
                    Egy nagy csoport
                  </h4>
                  <p>
                    Abban az esetben, ha minden páros minden párossal játszana, 10 páros
                    esetén 45, 11 esetén 55, 12 esetén 66 csoportmeccs lenne, ez csak úgy
                    lenne lehetséges, ha egy meccs nem két nyert hagyományos szett lenne,
                    hanem vagy rövidített szettek, vagy egy nyert szett. Ez dupla
                    adminisztrációt, rengeteg cserét, csúszást, kiszámíthatatlanságot jelent,
                    illetve a tenisz hullámvölgyeihez sem annyira jól alkalmazkodik, ezért
                    csak akkor jó választás, ha a mindenki-mindenkivel mérkőzik elv felülírja
                    a felsorolt hátrányokat.
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-C3">
                      C3
                    </span>
                    Svájci
                  </h4>
                  <p>
                    Svájci módszer az 1895-ös Zürich-i sakkversenyből származik, a lényege,
                    hogy körök vannak és minden körben olyan versenyzőket/párokat sorsol
                    össze, akik hasonló eredményekkel rendelkeznek. Az első körben a
                    párosítások véletlenszerűek, majd a második körben az első kör győztesei
                    az első kör többi győztesével játszanak, a vesztesek a vesztesekkel. A
                    harmadik körben a 2–0, 1–1 és 0–2 mérlegűek játszanak egymással.
                    Páratlan szám esetén egy valaki kimarad, ilyenkor plusz fél vagy plusz
                    egy pontot kap, alapelv, hogy az egész verseny alatt ne forduljon elő,
                    hogy egy valaki kétszer is kimaradjon egy körből, illetve hogy ugyanazzal
                    mérkőzzön kétszer.
                  </p>

                  <p>
                    A svájci módszer {String.raw`$\log_2(n)$`} kör alapján tud győztest
                    hirdetni és egy rangsort felállítani, az önszervezés miatt ugyan nem
                    játszik mindenki mindenkivel, viszont egy idő után hasonló erejű párosok
                    mérkőznek, ami jó meccseket eredményez. 12 csapatnál a{' '}
                    {String.raw`$\log_2(12) \approx 3{,}58$`} azaz 4 kör alatt már felállna
                    egy sorrend, ez {String.raw`$4 \cdot 6 = 24$`} meccs. Ez benne van a
                    keretben és nincs rá ok, hogy miért ne lehetne 5. kört csinálni, az pont
                    30. (10 csapatnál {String.raw`$\log_2(10) = 3{,}32$`} tehát szintén 4
                    kör a minimum, de akár 5–6 is belefér a keretbe: 4→20, 5→25, 6→30
                    meccs.)
                  </p>

                  <p>
                    A csoportos körmérkőzésekkel szemben viszont ez nem, vagy nehezebben
                    tervezhető, hiszen a párosítások dinamikusan alakulnak. Egy kör egy
                    napot jelent {String.raw`$2 \cdot 3 = 6$`} meccs, azaz a másnap
                    párosításai a délutáni meccsek eredményeitől is függnek. Illetve
                    előfordulhat, hogy az utolsó körre már van biztos győztes és nem az első
                    helyért megy a meccs, valamint nem biztos, hogy lesz egy előre bejelentett
                    döntő. Továbbá előfordulhat döntetlen pontállás (ugyanaz a győzelmek és
                    vereségek száma), ilyenkor többféle megoldás lehet (nyert gamek, le kell
                    játszani, stb…).
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-C4">
                      C4
                    </span>
                    Americano
                  </h4>
                  <p>
                    Az americano lényege, hogy rövid meccsekről van szó (egy szett, vagy 20
                    perc) és egy előre meghatározott sorrend szerint cserélődnek a párok és a
                    győzelmeket számolják. Ezt csináltuk tavaly az első/utolsó nap bemelegítő
                    és levezető kupáján, de a párokat is cserélgettük.
                  </p>
                </div>

                <div className="option">
                  <h4 className="option-title">
                    <span className="option-tag" id="opt-C5">
                      C5
                    </span>
                    Mexikói
                  </h4>
                  <p>
                    Ugyanaz, mint az americano, azzal a különbséggel, hogy a meccs
                    párosítások nem egy fix sorrend szerint, hanem svájci módszerrel
                    történnek.
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION III: SZAVAZÁS */}
            <section className="section">
              <div className="col">
                <header className="section-header">
                  <span className="section-num">Harmadik rész</span>
                  <h2>Szavazás</h2>
                </header>

                <p>
                  A korábban felsorolt lehetőségek közül érdemes úgy dönteni, hogy egy
                  döntési fát építünk, ahol az összes elágazásnál mindenki felállít egy
                  preferencia sorrendet. Az így kapott szavazatok alapján minden elágazásnál
                  a legnépszerűbb lehetőség lesz a döntés, így nem aprózódnak el a szavazatok
                  és annak ellenére, hogy az előző döntésben mást tartott valaki jobbnak a
                  következő döntés részleteibe ugyanúgy lesz beleszólása.
                </p>
                <p>
                  Bónusz kérdés Tóth Gyuri ötlete nyomán: legyen-e egy közös feltöltő felület képeknek a weboldalon (ennek a költsége ~63 forint/Gb évente).
                </p>
              </div>

              <CikkDecisionTree />
            </section>
          </article>
        </main>

        <footer className="colophon">
          <p>Nem Magyarország Kormánya megbízásából készült.</p>
        </footer>
      </div>

      {/* Floating tooltip for hoverable variables in formulas (positioned by the hook) */}
      <div id="var-tooltip" ref={tooltipRef} role="tooltip" aria-hidden="true" />
    </div>
  );
}